/** Pure helpers and the request for the community sensor history page (reports + charts). */

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDay = day => {
    const [year, month, date] = day.split('-').map(Number);
    return Date.UTC(year, month - 1, date);
};

const formatDay = ms => new Date(ms).toISOString().slice(0, 10);

/** Today as yyyy-mm-dd in the browser's time zone. */
export const todayLocal = () => {
    const now = new Date();
    return formatDay(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

/** A window of whole days that ends on endDate (yyyy-mm-dd). */
export const windowEndingAt = (endDate, days) => ({
    dateFrom: formatDay(parseDay(endDate) - (days - 1) * DAY_MS),
    dateTo: endDate,
    days
});

/** The previous (-1) or next (+1) window of the same length. */
export const shiftWindow = (window, direction) => {
    const shift = direction * window.days * DAY_MS;
    return {
        dateFrom: formatDay(parseDay(window.dateFrom) + shift),
        dateTo: formatDay(parseDay(window.dateTo) + shift),
        days: window.days
    };
};

export const isLastWindow = (window, today) => parseDay(window.dateTo) >= parseDay(today);

/** Finer detail for short periods keeps the chart readable and the request small. */
export const defaultAggregation = days => {
    if (days <= 1) return 'RAW';
    if (days <= 7) return 'MIN_30';
    if (days <= 31) return 'HOUR';
    return 'DAY';
};

export const AGGREGATIONS = ['RAW', 'MIN_30', 'HOUR', 'DAY'];

export const buildReportBody = ({sensorId, dateFrom, dateTo, aggregationPeriod, withBattery = false}) => ({
    sensorId,
    dateFrom,
    dateTo,
    aggregationPeriod,
    withBattery
});

export const fetchSensorReport = async (body, signal) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/measurements/sensor`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            Authorization: sessionStorage.getItem('agronetToken'),
            Local: localStorage.getItem('lang') || 'en'
        },
        body: JSON.stringify(body),
        signal
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
};

/** What the report measures, e.g. temperature and humidity, each with its columns. */
export const parameterOptions = report => {
    if (!report || !report.columns) return [];
    const groups = [];
    const byKey = {};
    report.columns.forEach(column => {
        if (!byKey[column.parameterKey]) {
            byKey[column.parameterKey] = {parameterKey: column.parameterKey, unit: column.unit, columns: []};
            groups.push(byKey[column.parameterKey]);
        }
        byKey[column.parameterKey].columns.push(column);
    });
    return groups;
};

const number = value => (value === null || value === undefined || value === '' ? null : Number(value));

/** Points for one parameter, oldest first: {time, min, avg, max} (or the single statistic that exists). */
export const toChartSeries = (report, parameterKey) => {
    if (!report || !report.rows) return [];
    const columns = parameterOptions(report).filter(option => option.parameterKey === parameterKey)
        .reduce((all, option) => all.concat(option.columns), []);
    if (columns.length === 0) return [];
    return report.rows
        .map(row => {
            const point = {time: new Date(row.timestampFrom).getTime()};
            columns.forEach(column => {
                point[column.statisticKey || 'value'] = number(row.values ? row.values[column.key] : null);
            });
            return point;
        })
        .sort((a, b) => a.time - b.time);
};

const firstNumber = (...values) => {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
    }
    return null;
};

/** Lowest, highest and average over a series. */
export const summarize = series => {
    const lows = series.map(p => firstNumber(p.min, p.avg, p.value, p.sum)).filter(v => v !== null);
    const highs = series.map(p => firstNumber(p.max, p.avg, p.value, p.sum)).filter(v => v !== null);
    const averages = series.map(p => firstNumber(p.avg, p.value, p.sum)).filter(v => v !== null);
    return {
        min: lows.length ? Math.min(...lows) : null,
        max: highs.length ? Math.max(...highs) : null,
        avg: averages.length ? averages.reduce((a, b) => a + b, 0) / averages.length : null,
        count: series.length
    };
};
